import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.css'],
    standalone: false
})
export class FooterComponent implements OnInit, AfterViewInit, OnDestroy {
  currentYear: any;

  private cleanups: Array<() => void> = [];

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.currentYear = new Date().getFullYear();
  }

  ngOnInit(): void {}

  /**
   * Scroll reveal + cursor parallax. Content is visible by default; `js-anim`
   * opts into the hidden start state, so a broken observer degrades to "no
   * animation", never "no footer". Listeners run outside the zone.
   */
  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = this.host.nativeElement.querySelector('.rs-endcap') as HTMLElement | null;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('is-in');
      return;
    }

    el.classList.add('js-anim');

    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      el.classList.add('is-in');
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          el.classList.add('is-in');
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -8% 0px' }
      );
      this.zone.runOutsideAngular(() => io.observe(el));
      this.cleanups.push(() => io.disconnect());
    }

    let queued = false;
    let x = 0,
      y = 0;
    const apply = () => {
      queued = false;
      el.style.setProperty('--px', x.toFixed(4));
      el.style.setProperty('--py', y.toFixed(4));
    };
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      x = (e.clientX - r.left) / r.width - 0.5;
      y = (e.clientY - r.top) / r.height - 0.5;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };
    this.zone.runOutsideAngular(() => {
      el.addEventListener('mousemove', onMove, { passive: true });
    });
    this.cleanups.push(() => el.removeEventListener('mousemove', onMove));
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
  }
}
